// frontend/src/utils/acquisitionCapture.js
//
// THE store half of the marketing-attribution capture (#100): read the landing
// URL's acquisition params, validate each, and keep the valid ones in
// sessionStorage FIRST-WRITER-WINS. Nothing else - no URL rewrite, no event.
//
// Two callers, on purpose:
//
//  - src/index.js, SYNCHRONOUSLY at boot, right after the `/index.html`
//    rewrite and before React exists (#464 / #465). A prerendered page is
//    painted at parse time but hydrated only once its route chunk has loaded
//    (up to HYDRATION_PRELOAD_TIMEOUT_MS), and React effects run only after
//    that. A paid visitor who taps a snapshot link in that window leaves on a
//    native navigation - and with an effect-only capture, the `?s=` code (or
//    the `?ref=` friend code) left with them. Captured at boot, it is already
//    in this tab's storage.
//  - components/CaptureAcquisition.js, in its mount effect, again: idempotent
//    (first writer wins, so the second call never changes a stored value) and
//    the belt for any boot path that did not run the line in index.js. That
//    component still owns the URL clean-up and the one `utm_visit` event.
//
// The params, each validated before it is stored (an invalid value is dropped
// SILENTLY - no console line, no fallback):
//   s, c -> 'acq_s', 'acq_c'  opaque short codes, ^[A-Za-z0-9]{1,8}$;
//   src  -> 'acq_src'         the email-CTA tag, lowercase + underscore only;
//   ref  -> 'acq_ref'         a friend (partner) code, upper-cased
//                              (utils/friendCode.js captureRefValue).
//
// Consent: unchanged. This is first-party sessionStorage the visit's own
// requests read later; it sends nothing anywhere. The codes are opaque here;
// the code->surface taxonomy lives only in the backend. A friend code is never
// logged and never reaches an analytics payload (utils/friendCode.js).
//
// Never throws: a hostile or absent sessionStorage, or a malformed URL, can
// never break boot.

import { ACQ_REF_KEY, captureRefValue } from "./friendCode";

// The literal is test-pinned against its twins (utils/adVisitor.js and the
// ad-hero hold head script in public/index.html) - keep the line's exact form.
const CODE_RE = /^[A-Za-z0-9]{1,8}$/;

// `src` is a fixed-vocabulary email-CTA tag ('trial_ending', 'winback', ...),
// not an opaque code, so it gets its own shape: lowercase + underscore, and no
// digits at all. That last part is load-bearing rather than cosmetic - the
// backend's param sanitizer drops any all-digit STRING as a suspected phone
// number, so a numeric tag would be silently discarded server-side.
const SRC_RE = /^[a-z_]{1,32}$/;

/** sessionStorage keys this module writes. */
export const ACQ_KEYS = Object.freeze({
  s: "acq_s",
  c: "acq_c",
  src: "acq_src",
  ref: ACQ_REF_KEY,
});

/** The URL params the capture reads (and CaptureAcquisition strips). */
export const ACQUISITION_PARAMS = Object.freeze(["s", "c", "src", "ref"]);

const isValidCode = (v) => typeof v === "string" && CODE_RE.test(v);

const isValidSrc = (v) => typeof v === "string" && SRC_RE.test(v);

// First-writer-wins write: only set when the key is currently empty.
const storeIfAbsent = (storage, key, value) => {
  try {
    if (!storage.getItem(key)) {
      storage.setItem(key, value);
    }
  } catch (e) {
    /* storage unavailable - skip silently */
  }
};

/**
 * Store this landing URL's valid acquisition params, first writer wins.
 * Idempotent; never throws; never touches the URL or the network.
 *
 * @param {Window} [win=window] the window to read (injectable for tests).
 */
export function captureAcquisitionParams(win = window) {
  try {
    const params = new URLSearchParams(win.location.search);
    const storage = win.sessionStorage;
    const rawS = params.get("s");
    const rawC = params.get("c");
    const rawSrc = params.get("src");

    if (isValidCode(rawS)) storeIfAbsent(storage, ACQ_KEYS.s, rawS);
    if (isValidCode(rawC)) storeIfAbsent(storage, ACQ_KEYS.c, rawC);
    // Same first-writer-wins rule: the first tagged arrival of the visit owns
    // the attribution. A malformed src is either a typo in an email template
    // or somebody probing, and neither deserves a stored value.
    if (isValidSrc(rawSrc)) storeIfAbsent(storage, ACQ_KEYS.src, rawSrc);
    // Friend code: stored upper-cased. Never read back by anything that
    // reports to analytics.
    const ref = captureRefValue(params.get("ref"));
    if (ref) storeIfAbsent(storage, ACQ_KEYS.ref, ref);
  } catch (e) {
    // Attribution capture must never break the app.
  }
}
